<template>
  <div v-if="isLoading" class="flex justify-center items-center h-48">
    <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
  </div>

  <div v-else-if="post" class="space-y-6">
    <!-- 主要貼文 -->
    <Post 
      :post="post" 
      :isLoggedIn="isLoggedIn"
      :showDetailOnClick="false"
      :isDetailPage="true"
      @like="handleLike"
      @bookmark="handleBookmark"
      @showMore="handleShowMore"
      @edit="handleEdit"
      @delete="handleDelete"
      @pin="handlePin"
      @unpin="handleUnpin"
    />

    <!-- 留言區標題 -->
    <div v-if="comments.length > 0" class="flex items-center space-x-2 pt-6">
      <i class="fas fa-comments text-wood-600"></i>
      <h3 class="text-lg font-semibold text-wood-800">留言 ({{ comments.length }})</h3>
    </div>

    <!-- 留言列表 -->
    <div v-if="comments.length > 0" class="space-y-4 pl-4 border-l-2 border-wood-200">
      <Post 
        v-for="comment in comments"
        :key="comment.id"
        :post="comment"
        :isLoggedIn="isLoggedIn"
        :showDetailOnClick="false"
        @like="handleLike"
        @bookmark="handleBookmark"
        @showMore="handleShowMore"
        @edit="handleEdit"
        @delete="handleDelete"
        class="ml-4"
      />
    </div>

    <!-- 沒有留言時的提示 -->
    <div v-else class="text-center py-8">
      <i class="fas fa-comment-slash text-wood-400 text-3xl mb-2"></i>
      <p class="text-wood-600">尚無留言</p>
    </div>

    <!-- 新增留言區（已登入使用者）-->
    <div v-if="isLoggedIn" class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow">
      <h4 class="text-wood-800 font-semibold mb-4">發表留言</h4>
      <div class="space-y-4">
        <textarea 
          v-model="newComment"
          placeholder="分享你的想法..."
          class="w-full px-4 py-3 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/50 text-wood-800 placeholder-wood-500 resize-none"
          rows="3"
        ></textarea>
        <div class="flex justify-end">
          <button 
            @click="submitComment"
            :disabled="!newComment.trim()"
            class="px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:bg-gradient-to-r disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:transform-none"
          >
            發表留言
          </button>
        </div>
      </div>
    </div>

    <!-- 未登入提示 -->
    <div v-else class="bg-wood-100 rounded-2xl p-6 text-center">
      <i class="fas fa-sign-in-alt text-wood-500 text-2xl mb-2"></i>
      <p class="text-wood-700 mb-4">請登入後發表留言</p>
      <button @click="userStore.googleLogin" class="inline-block px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg">
        使用Google登入
      </button>
    </div>
  </div>

  <!-- 錯誤狀態 -->
  <div v-else class="text-center py-12">
    <i class="fas fa-exclamation-triangle text-wood-400 text-4xl mb-4"></i>
    <h3 class="text-xl text-wood-700 mb-2">找不到此貼文</h3>
    <p class="text-wood-600 mb-6">此貼文可能已被刪除或不存在</p>
    <a href="/" class="inline-block px-6 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg">
      返回首頁
    </a>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { usePostsStore } from '../stores/posts.js'
import { useUserStore } from '../stores/user.js'
import Post from './Post.vue'

const props = defineProps({
  postId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['edit'])

const postsStore = usePostsStore()
const userStore = useUserStore()
const newComment = ref('')

// 計算屬性
const isLoading = computed(() => postsStore.isLoadingDetail)
const post = computed(() => postsStore.currentPost)
const comments = computed(() => postsStore.currentComments)
const isLoggedIn = computed(() => userStore.isLoggedIn)

// 載入貼文詳情和留言的函數
const loadPostData = async (postId) => {
  console.log('載入貼文資料，ID:', postId)
  await postsStore.loadPostDetail(postId)
  await postsStore.loadPostComments(postId)
}

// 組件掛載時載入
onMounted(async () => {
  await loadPostData(props.postId)
})

// 監聽 postId 變更
watch(() => props.postId, async (newPostId) => {
  if (newPostId) {
    await loadPostData(newPostId)
  }
}, { immediate: false })

// 事件處理
const handleLike = async (postId) => {
  await postsStore.likePost(postId)
}

const handleBookmark = async (postId) => {
  await postsStore.bookMark(postId)
}

const handleShowMore = (postId) => {
  postsStore.showMore(postId)
}

const submitComment = async () => {
  if (!newComment.value.trim()) return
  
  try {
    await postsStore.submitComment(props.postId, newComment.value.trim())
    newComment.value = ''
    // 重新載入留言
    await postsStore.loadPostComments(props.postId)
  } catch (error) {
    console.error('提交留言失敗:', error)
  }
}

const handleEdit = (post) => {
  console.log('PostDetail: 處理編輯事件，貼文:', post)
  emit('edit', post)
}

const handleDelete = async (postId) => {
  if (confirm('您確定要刪除這則貼文嗎？此操作無法復原。')) {
    await postsStore.deletePost(postId)
  }
}

const handlePin = async (postId) => {
  try {
    await postsStore.pinPost(postId)
    // 重新載入貼文以更新置頂狀態
    await postsStore.loadPostDetail(props.postId)
  } catch (error) {
    console.error('置頂貼文失敗:', error)
  }
}

const handleUnpin = async (postId) => {
  try {
    await postsStore.unpinPost(postId)
    // 重新載入貼文以更新置頂狀態
    await postsStore.loadPostDetail(props.postId)
  } catch (error) {
    console.error('取消置頂失敗:', error)
  }
}
</script>

<style scoped>
/* 貼文陰影 */
.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

/* 陰陽太極圖 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

/* 動畫效果 */
@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}

/* 木質色彩系統 */
.text-wood-50 { color: #faf8f3; }
.text-wood-100 { color: #f4f0e6; }
.text-wood-200 { color: #e8dcc0; }
.text-wood-300 { color: #dcc89a; }
.text-wood-400 { color: #c4a674; }
.text-wood-500 { color: #a68b5b; }
.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }
.text-wood-900 { color: #2d261f; }

.bg-wood-50 { background-color: #faf8f3; }
.bg-wood-100 { background-color: #f4f0e6; }
.bg-wood-200 { background-color: #e8dcc0; }
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-400 { background-color: #c4a674; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }
.bg-wood-900 { background-color: #2d261f; }

.border-wood-200 { border-color: #e8dcc0; }
.border-wood-300 { border-color: #dcc89a; }
.border-wood-500 { border-color: #a68b5b; }

/* 漸變背景效果 */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-wood-500 {
  --tw-gradient-from: #a68b5b;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(166, 139, 91, 0));
}

.to-wood-600 {
  --tw-gradient-to: #8b7355;
}

.hover\:from-wood-600:hover { --tw-gradient-from: #8b7355; }
.hover\:to-wood-700:hover { --tw-gradient-to: #6d5a47; }
</style>